import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'warning';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Show a loading spinner and disable the button. */
  loading?: boolean;
  /** Icon rendered before the children. */
  leftIcon?: ReactNode;
  /** Icon rendered after the children. */
  rightIcon?: ReactNode;
  /** Render as an icon-only square button (no label). */
  iconOnly?: boolean;
}

/**
 * The single button primitive for RF-CRM.
 *
 * Variants:
 *  - primary  — filled accent (default for the page's main action)
 *  - secondary — 1px border, transparent background (peer actions)
 *  - ghost    — no border, hover background (toolbar / icon buttons)
 *  - danger   — filled muted-rose (destructive only — Delete, Cancel order)
 *  - warning  — filled amber (caution actions — Pause)
 *
 * Use `iconOnly` for action toolbars (e.g. row actions). Provide an aria-label
 * via standard button props for screen readers.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    leftIcon,
    rightIcon,
    iconOnly = false,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = Boolean(disabled || loading);

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={clsx(
        styles.button,
        styles[`variant_${variant}`],
        styles[`size_${size}`],
        iconOnly && styles.iconOnly,
        loading && styles.loading,
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span className={styles.spinner} aria-hidden="true" />
      ) : (
        leftIcon && <span className={styles.icon}>{leftIcon}</span>
      )}
      {!iconOnly && children !== undefined && (
        <span className={styles.label}>{children}</span>
      )}
      {!loading && rightIcon && <span className={styles.icon}>{rightIcon}</span>}
    </button>
  );
});
